import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_SERVICES, type Connection, type ConnectionProvider, type ProjectServices, type ProjectServicesConfig } from '@one-deployment/api'
import { Button, Card, Segmented, Select, SkeletonRows } from '@/components/ui'
import { ErrorState } from '@/components/status'
import { enc, get, put } from '@/lib/api'
import { useIntegrations } from './Settings'

const NAMES: Partial<Record<ConnectionProvider, string>> = {
  cloudflare: 'Cloudflare', vercel: 'Vercel', trigger: 'Trigger.dev', supabase: 'Supabase',
  expo: 'Expo', 'google-play': 'Google Play', 'app-store': 'App Store Connect', firebase: 'Firebase',
}

function used(c: ProjectServicesConfig): ConnectionProvider[] {
  const out = new Set<ConnectionProvider>()
  for (const v of [c.frontend, c.backend, c.jobs, c.database]) if (v !== 'none') out.add(v)
  if (c.mobile) ['expo', 'google-play', 'app-store'].forEach((p) => out.add(p as ConnectionProvider))
  if (c.push) out.add('firebase')
  return [...out]
}

/**
 * What the project runs on and which connected account each service uses.
 * Shared by Create project and the project's settings.
 */
export function ServicesForm({ value, onChange }: { value: ProjectServicesConfig; onChange: (v: ProjectServicesConfig) => void }) {
  const ints = useIntegrations()
  const conns = ints.data?.connections ?? []
  const set = (patch: Partial<ProjectServicesConfig>) => onChange({ ...value, ...patch })
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[10rem_1fr] items-center gap-x-4 gap-y-3 text-sm">
        <span className="text-fg-muted">Frontend</span>
        <Segmented value={value.frontend} onChange={(v) => set({ frontend: v })} options={[{ value: 'cloudflare', label: 'Cloudflare Pages' }, { value: 'vercel', label: 'Vercel' }, { value: 'none', label: 'None' }]} />
        <span className="text-fg-muted">Backend</span>
        <Segmented value={value.backend} onChange={(v) => set({ backend: v })} options={[{ value: 'cloudflare', label: 'Cloudflare Workers' }, { value: 'vercel', label: 'Vercel' }, { value: 'none', label: 'None' }]} />
        <span className="text-fg-muted">Long-running jobs</span>
        <Segmented value={value.jobs} onChange={(v) => set({ jobs: v })} options={[{ value: 'cloudflare', label: 'Cloudflare Workflows' }, { value: 'trigger', label: 'Trigger.dev' }, { value: 'none', label: 'None' }]} />
        <span className="text-fg-muted">Database</span>
        <Segmented value={value.database} onChange={(v) => set({ database: v })} options={[{ value: 'supabase', label: 'Supabase' }, { value: 'none', label: 'None' }]} />
        <span className="text-fg-muted">Mobile app</span>
        <Segmented value={value.mobile ? 'yes' : 'no'} onChange={(v) => set({ mobile: v === 'yes' })} options={[{ value: 'yes', label: 'Yes · Expo, Play, App Store' }, { value: 'no', label: 'No' }]} />
        <span className="text-fg-muted">Push notifications</span>
        <Segmented value={value.push ? 'yes' : 'no'} onChange={(v) => set({ push: v === 'yes' })} options={[{ value: 'yes', label: 'Yes · Firebase' }, { value: 'no', label: 'No' }]} />
      </div>

      {used(value).length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
          <div className="text-xs text-fg-subtle">Accounts</div>
          {used(value).map((p) => <AccountPick key={p} provider={p} conns={conns.filter((c) => c.provider === p)} value={value.accounts[p] ?? null}
            onChange={(id) => set({ accounts: { ...value.accounts, [p]: id } })} />)}
        </div>
      )}
    </div>
  )
}

function AccountPick({ provider, conns, value, onChange }: { provider: ConnectionProvider; conns: Connection[]; value: string | null; onChange: (id: string | null) => void }) {
  const def = conns.find((c) => c.isDefault)
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className={`size-2 rounded-full ${conns.length ? 'bg-ok' : 'bg-err'}`} />
      <span className="w-40">{NAMES[provider]}</span>
      {conns.length === 0 ? (
        <span className="text-xs text-fg-faint">No account connected. <Link to="/settings/connections" className="text-accent hover:underline">Add one on Connections</Link></span>
      ) : (
        <Select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className="min-w-56">
          <option value="">Default{def ? ` · ${def.label}` : ''}</option>
          {conns.map((c) => <option key={c.id} value={c.id}>{c.label}{c.account && c.account !== c.label ? ` (${c.account})` : ''}</option>)}
        </Select>
      )}
    </div>
  )
}

/** Settings → Services: confirm what was detected, or change it later. */
export function Services({ project }: { project: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['project', project, 'services'], queryFn: ({ signal }) => get<ProjectServices>(`/api/projects/${enc(project)}/services`, signal) })
  const [draft, setDraft] = useState<ProjectServicesConfig | null>(null)
  useEffect(() => { if (q.data && !draft) setDraft(q.data.config ?? q.data.detected ?? DEFAULT_SERVICES) }, [q.data, draft])
  const save = useMutation({
    mutationFn: (v: ProjectServicesConfig) => put<ProjectServices>(`/api/projects/${enc(project)}/services`, v),
    onSuccess: (r) => { qc.setQueryData(['project', project, 'services'], r); setDraft(r.config) },
  })
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />
  if (!q.data || !draft) return <SkeletonRows rows={6} />
  const d = q.data
  const dirty = JSON.stringify(draft) !== JSON.stringify(d.config)
  return (
    <Card title="Services">
      <div className="flex flex-col gap-4 p-4">
        {!d.config && (
          <div className="rounded-md border border-accent/40 bg-accent/10 px-3 py-2 text-xs">
            {d.detected ? <>Read from the repo{d.evidence.length > 0 && <>: <span className="font-mono">{d.evidence.join(', ')}</span></>}. Check it and press Confirm.</> : 'Nothing detected in the repo; these are the defaults. Check them and press Confirm.'}
            {' '}Until confirmed, sessions get none of these keys.
          </div>
        )}
        <ServicesForm value={draft} onChange={setDraft} />
        {d.envNames.length > 0 && d.config && <p className="text-xs text-fg-subtle">Sessions get <span className="font-mono">{d.envNames.join(', ')}</span>. A change reaches running sessions when their container next starts.</p>}
        {save.error && <ErrorState error={save.error} compact />}
        <div className="flex gap-2">
          <Button variant="primary" disabled={save.isPending || (!dirty && !!d.config)} onClick={() => save.mutate(draft)}>{d.config ? 'Save' : 'Confirm'}</Button>
          {dirty && d.config && <Button onClick={() => setDraft(d.config)}>Undo</Button>}
        </div>
      </div>
    </Card>
  )
}
